'use client';

import { type MouseEvent, memo, type ReactNode, useMemo, useState } from 'react';

import {
  TRUNCATED_TEXT_TOOLTIP_CONTENT_CLASS,
  TruncatedTextWithTooltip,
} from '@air/component-truncated-text-with-tooltip';
import { Tooltip } from '@air/primitive-tooltip';
import { cn } from '@air/utils-tailwind';

import {
  getListViewGridTemplateColumns,
  LIST_VIEW_BLANK_CELL,
  type ListViewColumnField,
} from '~/constants/listViewColumns';
import { type ListViewRowInfo } from '~/utils/listViewRowInfo';

export type ListItemRowBaseProps = {
  actionTestId: string;
  isSelected?: boolean;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  onDoubleClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  rowInfo: ListViewRowInfo;
  testId: string;
  thumbnail: ReactNode;
  title: string;
  visibleColumns: ListViewColumnField[];
};

export const ListItemRowBase = memo(
  ({
    actionTestId,
    isSelected,
    onClick,
    onDoubleClick,
    rowInfo,
    testId,
    thumbnail,
    title,
    visibleColumns,
  }: ListItemRowBaseProps) => {
    const [isTitleOverflowing, setIsTitleOverflowing] = useState(false);
    const gridTemplateColumns = useMemo(() => getListViewGridTemplateColumns(visibleColumns), [visibleColumns]);

    // Inline minWidth/overflow on the grid row are required for the flex/grid truncation chain —
    // Tailwind min-w-0/overflow-hidden alone fail the WorkspaceList Cypress overflow assertion.
    return (
      <div className="relative w-full min-w-0 overflow-hidden" data-testid={testId} style={{ minWidth: 0 }}>
        <div
          className={cn(
            'grid w-full min-w-0 items-center overflow-hidden border-neutral-4 border-b transition-colors hover:bg-neutral-3',
            isSelected && 'bg-brand-3',
          )}
          style={{ gridTemplateColumns, minWidth: 0, overflow: 'hidden' }}
        >
          <div className="flex min-w-0 items-center gap-2 overflow-hidden px-2 py-1.5" style={{ minWidth: 0 }}>
            {thumbnail}
            <TruncatedTextWithTooltip
              className="flex-1 font-medium text-12 text-neutral-12"
              data-testid="list-item-title"
              onOverflowChange={setIsTitleOverflowing}
              text={title}
            />
          </div>

          {visibleColumns.map((field) => {
            const value = rowInfo[field];
            const isBlank = value === LIST_VIEW_BLANK_CELL;

            return (
              <div
                className={cn(
                  'min-w-0 truncate px-1.5 py-1.5 text-12',
                  isBlank ? 'text-neutral-8' : 'text-neutral-11',
                  field === 'tagCount' || field === 'boardCount' || field === 'openCommentCount' ? 'text-right' : '',
                )}
                key={field}
              >
                {value}
              </div>
            );
          })}
        </div>

        <Tooltip
          className={TRUNCATED_TEXT_TOOLTIP_CONTENT_CLASS}
          label={isTitleOverflowing ? title : undefined}
          side="top"
          sideOffset={4}
        >
          <button
            aria-label={`Select ${title}`}
            className="absolute inset-0 size-full bg-transparent outline-none"
            data-testid={actionTestId}
            onClick={onClick}
            onDoubleClick={onDoubleClick}
            type="button"
          />
        </Tooltip>
      </div>
    );
  },
);

ListItemRowBase.displayName = 'ListItemRowBase';
